import type { AnimationEasing } from "../layout/types.js";
import {
  FONT_FAMILY_VALUES,
  TEXT_ALIGN_VALUES,
  TEXT_DECORATION_LINE_VALUES,
  TEXT_DECORATION_STYLE_VALUES,
  TEXT_TRANSFORM_VALUES,
  WHITE_SPACE_VALUES,
  type AnimationDirection,
  type AnimationFillMode,
  type FontFamilyValue,
  type FontWeightValue,
  type TextAlignValue,
  type TextTransformValue,
  type WhiteSpaceValue,
} from "../blueprint/types.js";

const ANIMATION_DIRECTION_VALUES = [
  "normal",
  "reverse",
  "alternate",
  "alternate-reverse",
] as const;

const ANIMATION_FILL_MODE_VALUES = [
  "none",
  "forwards",
  "backwards",
  "both",
] as const;

const ANIMATION_EASING_VALUES: readonly AnimationEasing[] = [
  "linear",
  "ease",
  "ease-in",
  "ease-out",
  "ease-in-out",
];

export function normalizeAnimationDirectionValue(
  raw: unknown,
): AnimationDirection {
  const s = raw == null ? "" : String(raw).trim();
  return (ANIMATION_DIRECTION_VALUES as readonly string[]).includes(s)
    ? (s as AnimationDirection)
    : "normal";
}

export function normalizeAnimationFillModeValue(
  raw: unknown,
): AnimationFillMode {
  const s = raw == null ? "" : String(raw).trim();
  return (ANIMATION_FILL_MODE_VALUES as readonly string[]).includes(s)
    ? (s as AnimationFillMode)
    : "forwards";
}

export function normalizeAnimationEasingValue(raw: unknown): AnimationEasing {
  const s = raw == null ? "" : String(raw).trim();
  return (ANIMATION_EASING_VALUES as readonly string[]).includes(s)
    ? (s as AnimationEasing)
    : "ease";
}

export function normalizeFontWeightValue(raw: unknown): FontWeightValue {
  const s = raw == null ? "" : String(raw).trim().toLowerCase();
  if (s === "thin" || s === "100") return "100";
  if (s === "extra light" || s === "extralight" || s === "200") return "200";
  if (s === "light" || s === "300") return "300";
  if (s === "regular" || s === "normal" || s === "400") return "400";
  if (s === "medium" || s === "500") return "500";
  if (s === "semi bold" || s === "semibold" || s === "600") return "600";
  if (s === "bold" || s === "700") return "700";
  if (s === "extra bold" || s === "extrabold" || s === "800") return "800";
  if (s === "black" || s === "900") return "900";
  return "400";
}

export function normalizeFontFamilyValue(raw: unknown): FontFamilyValue {
  const candidate = String(raw ?? "")
    .trim()
    .replace(/^['"]|['"]$/g, "");
  if (!candidate) {
    return FONT_FAMILY_VALUES[0];
  }
  const match = FONT_FAMILY_VALUES.find(
    (family) => family.toLowerCase() === candidate.toLowerCase(),
  );
  return (match ?? candidate) as FontFamilyValue;
}

export function normalizeTextAlignValue(raw: unknown): TextAlignValue {
  const candidate = String(raw ?? "")
    .trim()
    .toLowerCase();
  return (TEXT_ALIGN_VALUES as readonly string[]).includes(candidate)
    ? (candidate as TextAlignValue)
    : "left";
}

export function normalizeTextTransformValue(raw: unknown): TextTransformValue {
  const candidate = String(raw ?? "")
    .trim()
    .toLowerCase();
  return (TEXT_TRANSFORM_VALUES as readonly string[]).includes(candidate)
    ? (candidate as TextTransformValue)
    : "none";
}

export function normalizeWhiteSpaceValue(raw: unknown): WhiteSpaceValue {
  if (raw == null) return "normal";
  const trimmed = String(raw).trim();
  if ((WHITE_SPACE_VALUES as readonly string[]).includes(trimmed)) {
    return trimmed as WhiteSpaceValue;
  }
  const lower = trimmed.toLowerCase();
  if (lower === "preserve nowrap") {
    return "preserve nowrap";
  }
  if ((WHITE_SPACE_VALUES as readonly string[]).includes(lower)) {
    return lower as WhiteSpaceValue;
  }
  return "normal";
}

const TEXT_DECORATION_LINE_SELECTABLE = TEXT_DECORATION_LINE_VALUES.filter(
  (value) => value !== "none",
);

function parseTextDecorationLineSelection(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => String(entry).trim().toLowerCase())
      .filter((entry) =>
        (TEXT_DECORATION_LINE_SELECTABLE as readonly string[]).includes(entry),
      );
  }
  const trimmed = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (!trimmed || trimmed === "none") return [];
  return trimmed
    .split(/\s+/)
    .filter((entry) =>
      (TEXT_DECORATION_LINE_SELECTABLE as readonly string[]).includes(entry),
    );
}

function formatTextDecorationLineSelection(lines: readonly string[]): string {
  const unique = [
    ...new Set(
      lines
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) =>
          (TEXT_DECORATION_LINE_SELECTABLE as readonly string[]).includes(
            entry,
          ),
        ),
    ),
  ];
  if (unique.length === 0) return "none";
  return unique.join(" ");
}

export function normalizeTextDecorationLineValue(raw: unknown): string {
  return formatTextDecorationLineSelection(
    parseTextDecorationLineSelection(raw),
  );
}

export function normalizeTextDecorationStyleValue(raw: unknown): string {
  const candidate = String(raw ?? "")
    .trim()
    .toLowerCase();
  return (TEXT_DECORATION_STYLE_VALUES as readonly string[]).includes(candidate)
    ? candidate
    : "solid";
}
